// Shared ExprIR tree utilities consumed by BOTH emitters.
//
// First resident: identifier substitution for the `.update(fn)`
// lowering — `x.update((list) => list.map(...))` lowers to
// `x = <fn body with `list` replaced by the read of x>`, producing the
// SAME idiomatic native emit the hand-written `.set(x().map(...))`
// form produces (no IIFE, no closure-invocation noise).

import { hashServiceDeclsAsLegacy, itemLegacyList } from './registry-lookup'
import type { AttrIR, ChildIR, DeclIR, ExprIR, ParseResult, StructIR, TypeIR } from './types'

/**
 * Per-component map of `const name → scalar-literal value` for COMPONENT-SCOPE
 * consts, so a static-attr resolver (`<Image src={logo}>`, `<WebView html=…>`,
 * font, background) can resolve a named component-body const the same way it
 * already resolves a module-level one. Handles transitive aliases in source
 * order (`const a = '/x'; const b = a` → both resolve) — one forward pass
 * suffices because a `const` must be declared before it's referenced.
 *
 * Reads the `value` DeclIR (plain component-body consts; shipped with the
 * value-const widening). Only string / number / boolean literals + identifier
 * aliases to an already-known const are captured; anything else is skipped
 * (the static-attr path then falls through to its existing "needs static"
 * behavior, unchanged).
 */
export function buildComponentConstMap(decls: DeclIR[]): Map<string, string | number | boolean> {
  const map = new Map<string, string | number | boolean>()
  for (const d of decls) {
    if (d.kind !== 'value') continue
    const e = d.expr
    if (
      e.kind === 'literal' &&
      (typeof e.value === 'string' || typeof e.value === 'number' || typeof e.value === 'boolean')
    ) {
      map.set(d.name, e.value)
    } else if (e.kind === 'identifier') {
      const aliased = map.get(e.name)
      if (aliased !== undefined) map.set(d.name, aliased)
    }
  }
  return map
}

/**
 * True for an ExprIR whose surface form is a multi-operand operator
 * expression (binary / comparison / logical / ternary). Used by the
 * bitwise-op emit to decide whether an operand needs wrapping parens so
 * the JS-parsed grouping survives a target whose operator precedence
 * differs (Swift binds `&` tighter than `+`, the reverse of JS; Kotlin
 * infix functions bind looser than arithmetic). A simple atom (identifier /
 * literal / call / member / index / paren) never needs extra parens.
 */
export function isCompoundExpr(e: ExprIR): boolean {
  return (
    e.kind === 'binary' ||
    e.kind === 'comparison' ||
    e.kind === 'logical' ||
    e.kind === 'ternary'
  )
}

/**
 * True if the access CHAIN rooted at `e` contains an optional member link
 * (`a?.b`) anywhere along its object/callee spine. Drives `?.` PROPAGATION
 * in the emitters: once an optional link appears, every subsequent access
 * must also be `?.` — required for Kotlin (a plain `.c` on a nullable is a
 * type error) and valid for Swift. So `a?.b.c` emits `a?.b?.c` on both.
 */
export function chainHasOptional(e: ExprIR): boolean {
  if (e.kind === 'member') return e.optional === true || chainHasOptional(e.object)
  if (e.kind === 'index') return chainHasOptional(e.object)
  if (e.kind === 'call') return chainHasOptional(e.callee)
  return false
}

/** TypeIR for a SCALAR literal value (string / number / boolean), else null. */
export function scalarLiteralType(e: ExprIR): TypeIR | null {
  if (e.kind !== 'literal') return null
  if (typeof e.value === 'string') return { kind: 'string' }
  if (typeof e.value === 'number') {
    // The literal's own `float` mark says an integral value IS a Double (a `1.0`
    // in a Double column); reading only the value typed that field Int, so a
    // column whose first row was integral and a later one fractional
    // synthesized two row structs that could not share an array.
    return { kind: 'number', float: e.float === true || !Number.isInteger(e.value) }
  }
  if (typeof e.value === 'boolean') return { kind: 'boolean' }
  return null // null literal — can't type a field from it
}

/**
 * Anonymous all-scalar-literal object EXPRESSION (`{ id: 1, name: 'a' }`) →
 * the name of a SYNTHESIZED struct/data-class for that shape, creating +
 * registering it on first sight (in the caller-owned `structs` / `keys`
 * state). Returns null when ANY field value is not a scalar literal — the
 * caller keeps its existing tuple emit (no regression). Without this, an
 * anonymous object degrades to a labelled tuple, which is illegal Swift for
 * a single field and breaks tuple key-paths (`ForEach(id:)`) + Codable.
 *
 * SHARED by both emitters so the synthesized names (`__Obj0`, `__Obj1`, …)
 * line up across targets: identical algorithm + identical source traversal
 * order → identical name assignment. Shape key = sorted `name:typekind`
 * pairs (so same-shape literals share one struct; same-names-different-scalar-
 * types get distinct structs).
 *
 * Field types come from `scalarLiteralType` (a literal value) OR, when the
 * optional `inferField` callback is supplied, from inferring a NON-literal
 * field's expression — so `{ id: count(), name: label() }` (signal reads)
 * synthesizes a struct too, not just `{ id: 1, name: 'a' }`. Only SCALAR
 * inferred kinds (number / string / boolean) are accepted: the shapeKey
 * distinguishes those precisely, whereas array / typeRef / nested-object
 * kinds collide on the lossy key, so an inferred non-scalar bails (→ the
 * caller keeps its tuple emit, unchanged). A field that is neither a scalar
 * literal nor a scalar-inferred expression returns null (no regression).
 * `inferField` is a callback (not an `InferenceCtx` import) so this module
 * stays dependency-free of `infer-type` — the caller, which already imports
 * `inferType`, passes `(e) => inferType(e, ctx)`.
 */
/**
 * A DECLARED struct whose fields are a superset of the literal's, where every
 * field the literal omits is OPTIONAL. Returns its name, or null.
 *
 * The exact field-set index that both emitters consult first cannot see this
 * case, and the gap is common rather than exotic: a type with an optional field
 * is ordinary TS, and a literal that simply does not set it is the normal way
 * to write one. `type T = { a: string; b?: string }` with `signal<T>({ a: 'x' })`
 * missed the index, fell through to synthesis, and emitted
 * `var v: T = __Obj0(a: "x")` — a type the annotation says it is not. Swift
 * rejects that outright, so the shape simply did not build; a recursive type
 * (a ProseMirror document, where the leaf node has `text` and the branch has
 * `content`) hits it at every level.
 *
 * Ambiguity BAILS rather than guessing: if two declared structs both accept the
 * literal, there is no type context here to choose between them, and picking
 * one would be a silent mis-construction. Same rule the exact index already
 * uses for a field-set collision.
 */
export function subsetStructName(
  literalFields: readonly string[],
  structs: readonly StructIR[],
  isOptional: (t: TypeIR) => boolean,
): string | null {
  const given = new Set(literalFields)
  let found: string | null = null
  for (const s of structs) {
    const names = new Set(s.fields.map((f) => f.name))
    // Every field the literal sets must exist on the struct...
    let ok = true
    for (const g of given) {
      if (!names.has(g)) {
        ok = false
        break
      }
    }
    if (!ok) continue
    // ...and every field it does NOT set must be optional.
    for (const f of s.fields) {
      if (!given.has(f.name) && !isOptional(f.type)) {
        ok = false
        break
      }
    }
    if (!ok) continue
    if (found !== null) return null // ambiguous — two structs accept it
    found = s.name
  }
  return found
}

/**
 * One piece of a JSON literal lowering: either static JSON text, or a runtime
 * expression whose encoded form goes in that slot.
 */
export type JsonLiteralPart =
  | { static: string }
  | { dyn: ExprIR }
  | { array: ({ spread: ExprIR } | { parts: JsonLiteralPart[] })[] }

/**
 * Lower an object/array literal that sits in a JSON POSITION straight to JSON,
 * instead of routing it through struct synthesis.
 *
 * `<WebView data={…}>` immediately hands its value to `PyreonJSON.encode` /
 * `PyreonJson.encode`, so the value IS JSON — building a native struct for it is
 * a detour, and the detour fails on exactly the payloads JSON exists to carry.
 * An ECharts option object (`{ xAxis: { type: 'category', data: days }, yAxis:
 * {}, series: [{ type: 'bar', data: revenue() }] }`) has heterogeneous nested
 * shapes and empty objects; no struct can be synthesized for it, so the emit
 * fell back to a tuple — which is invalid Kotlin, and on Swift a non-`Codable`
 * value that `encode` cannot serialize. That is why `examples/native-viz`, the
 * charts webview example, did not build on Android.
 *
 * Static parts become JSON text at COMPILE time; anything else (a signal read,
 * a call, an identifier) becomes a hole the emitter fills with an `encode(…)`
 * interpolation, so live data still flows. Returns null if any part cannot be
 * represented — the caller then keeps its existing path unchanged.
 *
 * `undefined` maps to JSON `null`: JSON has no undefined, and omitting the key
 * instead would silently change the object's SHAPE, which a hosted page reading
 * `Object.keys` would see.
 */
export function buildJsonLiteralParts(expr: ExprIR): JsonLiteralPart[] | null {
  const parts: JsonLiteralPart[] = []
  const pushStatic = (output: JsonLiteralPart[], text: string): void => {
    const last = output[output.length - 1]
    if (last !== undefined && 'static' in last) last.static += text
    else output.push({ static: text })
  }
  const walk = (e: ExprIR, output: JsonLiteralPart[] = parts): boolean => {
    if (e.kind === 'literal') {
      const v = e.value
      if (v === null || v === undefined) pushStatic(output, 'null')
      else if (typeof v === 'string') pushStatic(output, JSON.stringify(v))
      else pushStatic(output, JSON.stringify(v))
      return true
    }
    if (e.kind === 'array') {
      // A spread contributes zero or more elements, not one encoded array.
      // Keep its fragments separate so emitters can omit empty fragments and
      // insert commas at runtime, including nested heterogeneous JSON arrays.
      if (e.elements.some((el) => el.kind === 'spread')) {
        const segments: ({ spread: ExprIR } | { parts: JsonLiteralPart[] })[] = []
        for (const el of e.elements) {
          if (el.kind === 'spread') segments.push({ spread: el.argument })
          else {
            const elementParts: JsonLiteralPart[] = []
            if (!walk(el, elementParts)) return false
            segments.push({ parts: elementParts })
          }
        }
        output.push({ array: segments })
        return true
      }
      pushStatic(output, '[')
      for (let i = 0; i < e.elements.length; i++) {
        if (i > 0) pushStatic(output, ',')
        if (!walk(e.elements[i]!, output)) return false
      }
      pushStatic(output, ']')
      return true
    }
    if (e.kind === 'object') {
      // A spread cannot be resolved to JSON text at compile time.
      if ((e.spreads?.length ?? 0) > 0) return false
      pushStatic(output, '{')
      for (let i = 0; i < e.fields.length; i++) {
        if (i > 0) pushStatic(output, ',')
        pushStatic(output, `${JSON.stringify(e.fields[i]!.name)}:`)
        if (!walk(e.fields[i]!.value, output)) return false
      }
      pushStatic(output, '}')
      return true
    }
    if (e.kind === 'paren') return walk(e.inner, output)
    // Anything else is a runtime value — leave a hole for the emitter to fill
    // with its own `encode(…)` interpolation.
    output.push({ dyn: e })
    return true
  }
  if (expr.kind !== 'object' && expr.kind !== 'array') return null
  return walk(expr) ? parts : null
}

/**
 * The per-module suffix every synthesized anonymous-object struct carries.
 *
 * Synthesized structs are declared at FILE scope with a counter name
 * (`__Obj0`, `__Obj1`, …), so two generated files in one Xcode target / one
 * Gradle source set (one Kotlin package) each declared `__Obj0` and the build
 * failed with `invalid redeclaration` / `Redeclaration`. They cannot simply be
 * made file-private: a synthesized struct is named in internal signatures (a
 * `@State` property's type, a helper's return type), and neither language lets
 * an internal declaration expose a private type. So the NAME is made unique
 * per module instead.
 *
 * Set by `transform()` for the duration of one emit, and only when the caller
 * identifies the module (`filename`) — a standalone in-memory emit keeps the
 * bare `__ObjN`, which is what a single file needs. The suffix hashes the
 * module's PARSED content, not its raw source text or its path: the path is
 * machine-dependent (the CLI passes an absolute one), and generated output
 * must not change with the checkout location — so the original fix hashed
 * the source string instead.
 *
 * That was one step short. Plain Mode's pre-pass (`transformPlain`) lowers
 * `let x = state(0)` to `const x = signal(0)` — and total-tracking wraps a
 * conditionally-read dep in a `void (…)` prologue — BEFORE `parsePyreon` ever
 * builds the IR, so a Plain-authored module and its hand-written classic twin
 * converge to the exact same `ParseResult` (same components, same structs) but
 * start from DIFFERENT raw source text (different imports, `let`/`state()` vs
 * `const`/`signal()`, an extra `void (…)` the classic twin never has). Hashing
 * the raw text gave the SAME logical module two different suffixes depending
 * on which dialect authored it — breaking the very "Plain and classic emit
 * byte-identically" guarantee PMTC is built around, since even a semantics-
 * preserving Plain-Mode rewrite of an existing file renamed every synthesized
 * struct. Hashing the PARSED IR instead means the suffix depends on what the
 * module DOES, not on which dialect spelled it out, while staying exactly as
 * path-independent as the source-text scheme it replaces (`ParseResult` never
 * carries the filename `parsePyreon` was called with).
 *
 * `warnings` and `aliasImports` are deliberately excluded from the hash input:
 * a Plain Mode warning message embeds the filename itself (`msg
 * (file:line:col)`), which would reintroduce the exact path-dependence this
 * suffix exists to avoid, and `aliasImports` is a `Map` (stringifies to `{}`,
 * contributing nothing). Two structurally-different files landing on the same
 * tag is no worse than it was under the old scheme: hashing raw source text
 * already collapsed two files with byte-identical source onto one tag, and a
 * module reduced to the exact same IR as another one already shares its
 * top-level declared names — the two would collide on those regardless of
 * this suffix.
 */
let _synthStructSuffix = ''

/** Run `fn` with `suffix` as the synthesized-struct suffix, restoring the previous one after. */
export function withSynthStructSuffix<T>(suffix: string, fn: () => T): T {
  const prev = _synthStructSuffix
  _synthStructSuffix = suffix
  try {
    return fn()
  } finally {
    _synthStructSuffix = prev
  }
}

/** The name of the `n`-th synthesized struct in the current emit. */
export function synthStructName(n: number): string {
  return `__Obj${n}${_synthStructSuffix}`
}

/**
 * A short, stable, identifier-safe tag for a module's PARSED content (FNV-1a,
 * 32-bit, over a JSON snapshot of the semantically-relevant IR fields — see
 * the doc comment on `_synthStructSuffix` for why this is keyed on the parsed
 * IR rather than the raw source text or the file path).
 */
export function moduleTag(parsed: ParseResult): string {
  // A plugin item whose emitter names a `legacyList` hashes as the entries of the closed array it replaced,
  // at that array's position and with the payload as the array element, so no emitted name moves with the
  // move. Any other plugin item is appended AFTER everything, and only when there is one.
  const lane = (name: string): unknown[] =>
    parsed.moduleItems.filter((item) => itemLegacyList(item) === name).map((item) => item.payload)
  const foreign = parsed.moduleItems.filter((item) => itemLegacyList(item) === undefined)
  const key = JSON.stringify(
    [
      parsed.components,
      parsed.enums,
      parsed.structs,
      parsed.moduleDecls,
      lane('stores'),
      lane('models'),
      lane('fieldMetas'),
      lane('features'),
      lane('zodSchemas'),
      parsed.helperFns,
      lane('styledComponents'),
      lane('rocketstyleComponents'),
      lane('attrsComponents'),
      ...(foreign.length > 0 ? [foreign] : []),
    ],
    hashServiceDeclsAsLegacy,
  )
  let h = 0x811c9dc5
  for (let i = 0; i < key.length; i++) {
    h ^= key.charCodeAt(i)
    h = Math.imul(h, 0x01000193) >>> 0
  }
  return h.toString(36)
}

/**
 * Why a field value cannot be given a struct-field type — the ONE place that
 * knows, so both emitters produce the same message for the same shape.
 *
 * Returns null when the value IS typeable (the caller should not warn).
 *
 * This exists because the failure is a CLASS, not a shape. The first cut of
 * this diagnostic enumerated the one shape that had been observed (an empty
 * array field) in the parser; a sweep across the synthesis frontier then found
 * five more that fail identically and just as silently — `null` and `undefined`
 * fields, a NESTED empty array, a mixed scalar array, an array of arrays. Every
 * one of them is an ordinary data model (`{ id, parent: null }` is a tree
 * node), and each would have needed its own parser rule. Asking the bail site
 * itself is one rule that covers all of them, and the next one too.
 */
export function explainUntypeableField(value: ExprIR): string | null {
  if (value.kind === 'array') {
    if (value.elements.length === 0) {
      return 'an empty array literal carries no element type, and guessing one would be contradicted by the first non-empty assignment'
    }
    const first = value.elements[0]!
    if (first.kind === 'array') {
      return 'an array of arrays has no synthesized element struct — nested list types are outside the synthesis frontier'
    }
    // Compare the literal VALUE types, not the IR kind — `1` and `'two'` are
    // both `kind: 'literal'`, so a kind comparison reports a mixed array as
    // homogeneous and falls through to the generic message.
    const kindOf = (el: ExprIR): string =>
      el.kind === 'literal' ? `literal:${typeof el.value}` : el.kind
    if (value.elements.some((el) => kindOf(el) !== kindOf(first))) {
      return 'the array mixes element types, so it has no single element type'
    }
    return 'the array element type could not be resolved'
  }
  if (value.kind === 'literal' && (value.value === null || value.value === undefined)) {
    return 'a `null`/`undefined` literal carries no type — native structs need a concrete (optional) field type, so annotate the declaration to say what it is optional OF'
  }
  if (value.kind === 'object') {
    return 'a nested object literal whose own fields are not all typeable (check the nested fields for the same causes)'
  }
  return null
}

export function synthLiteralStructName(
  fields: { name: string; value: ExprIR }[],
  structs: StructIR[],
  keys: Map<string, string>,
  inferField?: (e: ExprIR) => TypeIR,
): string | null {
  if (fields.length === 0) return null
  const typed: { name: string; type: TypeIR }[] = []
  for (const f of fields) {
    const t = synthFieldType(f.value, structs, keys, inferField)
    if (t === null) return null
    typed.push({ name: f.name, type: t })
  }
  const shapeKey = typed
    .map((f) => `${f.name}:${typeShapeKey(f.type)}`)
    .slice()
    .sort()
    .join(',')
  const existing = keys.get(shapeKey)
  if (existing !== undefined) return existing
  const name = synthStructName(structs.length)
  structs.push({ name, fields: typed })
  keys.set(shapeKey, name)
  return name
}

/**
 * A helper FUNCTION PARAMETER's type with every inline object shape in it
 * (`t: { c: string }`, `rows: { a: number }[]`, `m?: { … }`) replaced by the
 * struct the matching object LITERAL resolves to — a declared struct of that
 * shape when there is one (`declared`), else the synthesized `__ObjN` from the
 * shared literal registry.
 *
 * A call site passes `f({ c: "x" })`, which both emitters lower through that
 * registry to `__Obj0(c: "x")`. The parameter used to be typed context-free:
 * Swift spelled it a labelled TUPLE (a single-field one collapsed to the bare
 * field type, `_ t: String`, so `t.c` did not typecheck) and Kotlin
 * synthesized its OWN `PyreonHelpersT` — two nominal types for one shape, and
 * neither target compiled. Resolving the parameter through the SAME registry
 * key as the literal makes the two agree by construction.
 */
export function namedInlineParamType(
  t: TypeIR,
  declared: (fields: readonly { name: string; type: TypeIR }[]) => string | undefined,
  structs: StructIR[],
  keys: Map<string, string>,
): TypeIR {
  switch (t.kind) {
    case 'object': {
      const name = declared(t.fields) ?? synthTypedStructName(t.fields, structs, keys)
      return name === null ? t : { kind: 'typeRef', name, args: [] }
    }
    case 'array':
      return { kind: 'array', element: namedInlineParamType(t.element, declared, structs, keys) }
    case 'union':
      return {
        kind: 'union',
        branches: t.branches.map((b) => namedInlineParamType(b, declared, structs, keys)),
      }
    default:
      return t
  }
}

/**
 * The TYPE-directed twin of {@link synthLiteralStructName}: the synthesized
 * struct name for an inline object TYPE (`createFlow<{ label: string }>`).
 *
 * It must hit the SAME registry key a value literal of that shape produces, or
 * a generic and the literals it types name two different structs — Swift
 * emitted `PyreonFlowState<String>` beside `data: __Obj0(label:)`, Kotlin
 * `PyreonFlowState<Any>`, and neither compiled. Nested object fields register
 * their own struct first, exactly as a nested literal does.
 */
export function synthTypedStructName(
  fields: readonly { name: string; type: TypeIR }[],
  structs: StructIR[],
  keys: Map<string, string>,
): string | null {
  if (fields.length === 0) return null
  const lift = (t: TypeIR): TypeIR | null => {
    if (t.kind === 'object') {
      const nested = synthTypedStructName(t.fields, structs, keys)
      return nested === null ? null : { kind: 'typeRef', name: nested, args: [] }
    }
    if (t.kind === 'array') {
      const element = lift(t.element)
      return element === null ? null : { kind: 'array', element }
    }
    return t
  }
  const typed: { name: string; type: TypeIR }[] = []
  for (const f of fields) {
    const t = lift(f.type)
    if (t === null) return null
    typed.push({ name: f.name, type: t })
  }
  const shapeKey = typed
    .map((f) => `${f.name}:${typeShapeKey(f.type)}`)
    .slice()
    .sort()
    .join(',')
  const existing = keys.get(shapeKey)
  if (existing !== undefined) return existing
  const name = synthStructName(structs.length)
  structs.push({ name, fields: typed })
  keys.set(shapeKey, name)
  return name
}

/**
 * A field value's synthesized struct-field TypeIR — the recursive core of
 * `synthLiteralStructName`. A scalar literal / scalar-inferred expression maps
 * to its scalar kind (the original flat behavior); a NESTED all-scalar object
 * literal recurses to its OWN synthesized `__ObjN` struct and the field is
 * typed `typeRef` to it; an array of nested objects (or of scalar literals)
 * synthesizes the element type. Anything else (a function, a spread-bearing
 * object, a mixed / un-inferable array, a bare identifier the ctx can't type)
 * returns null → the CALLER keeps its tuple emit, unchanged (no regression).
 */
function synthFieldType(
  value: ExprIR,
  structs: StructIR[],
  keys: Map<string, string>,
  inferField?: (e: ExprIR) => TypeIR,
): TypeIR | null {
  const scalar = scalarLiteralType(value)
  if (scalar !== null) return scalar
  if (inferField !== undefined) {
    const inferred = inferField(value)
    if (
      inferred.kind === 'string' ||
      inferred.kind === 'boolean' ||
      inferred.kind === 'number'
    ) {
      return inferred
    }
  }
  // NESTED object literal → recurse into its own synthesized struct.
  if (value.kind === 'object' && (value.spreads?.length ?? 0) === 0) {
    const nested = synthLiteralStructName(value.fields, structs, keys, inferField)
    return nested === null ? null : { kind: 'typeRef', name: nested, args: [] }
  }
  // Array literal → array of the (homogeneous) element type — nested objects
  // (`[{ sub: {…} }]`) synthesize an element struct; scalar-literal elements
  // give a scalar element.
  if (value.kind === 'array') {
    const el = synthArrayElementType(value.elements, structs, keys, inferField)
    return el === null ? null : { kind: 'array', element: el }
  }
  return null
}

function synthArrayElementType(
  elements: ExprIR[],
  structs: StructIR[],
  keys: Map<string, string>,
  inferField?: (e: ExprIR) => TypeIR,
): TypeIR | null {
  if (elements.length === 0) return null
  // Array of object literals → element struct (first-element shape convention;
  // a mixed-shape array is out of scope and would emit divergent structs, so
  // require EVERY element to be an object literal).
  if (elements.every((e) => e.kind === 'object')) {
    const first = elements[0] as Extract<ExprIR, { kind: 'object' }>
    const nested = synthLiteralStructName(first.fields, structs, keys, inferField)
    return nested === null ? null : { kind: 'typeRef', name: nested, args: [] }
  }
  // Array of scalar literals → scalar element (require homogeneous kind).
  const firstScalar = scalarLiteralType(elements[0]!)
  if (
    firstScalar !== null &&
    elements.every((e) => {
      const s = scalarLiteralType(e)
      return s !== null && s.kind === firstScalar.kind
    })
  ) {
    return firstScalar
  }
  return null
}

/** Resolves a declared struct's fields by name, so a `typeRef` can be keyed as its SHAPE. */
export type StructResolver = (name: string) => readonly { name: string; type: TypeIR }[] | undefined

/**
 * Deterministic per-field shape token for `synthLiteralStructName`'s dedup key.
 * A `typeRef` (a nested synthesized struct) carries the nested struct NAME —
 * itself derived from the nested shape — so two different nested shapes under
 * the same field name never collide on the lossy key (the collision the
 * scalar-only key had). Recursive over arrays.
 */
export function typeShapeKey(
  t: TypeIR,
  resolve?: StructResolver,
  seen?: ReadonlySet<string>,
): string {
  switch (t.kind) {
    case 'number':
      return t.float === true ? 'number.f' : 'number'
    case 'string':
      return 'string'
    case 'boolean':
      return 'boolean'
    case 'typeRef': {
      // A reference to a struct the file declares IS that struct's shape. The
      // inline-object lift (and every user `type Foo = { … }`) turns an
      // anonymous `{ id: string }` into `ref:Foo`, so without expanding it the
      // SAME shape keyed two ways (`obj{…}` at an annotation site, `ref:Foo`
      // inside the declared parent) and the lookup missed — each site then
      // synthesized its own struct for one shape, and the targets disagreed on
      // which name a value carried. Expansion is guarded against cycles.
      const fields = t.args.length === 0 ? resolve?.(t.name) : undefined
      if (fields !== undefined && !(seen?.has(t.name) ?? false)) {
        return `obj{${structShapeKey(fields, resolve, new Set([...(seen ?? []), t.name]))}}`
      }
      return `ref:${t.name}`
    }
    case 'array':
      return `arr:${typeShapeKey(t.element, resolve, seen)}`
    // Extended for `structShapeKey`, which keys DECLARED structs. A struct
    // whose fields differ only inside a map/set/object/union must still key
    // apart, or the two collapse exactly as Int-vs-Double did.
    case 'set':
      return `set:${typeShapeKey(t.element, resolve, seen)}`
    case 'map':
      return `map:${typeShapeKey(t.key, resolve, seen)}:${typeShapeKey(t.value, resolve, seen)}`
    case 'object':
      return `obj{${structShapeKey(t.fields, resolve, seen)}}`
    case 'union':
      // Sorted so branch ORDER never changes the key.
      return `union<${t.branches.map((b) => typeShapeKey(b, resolve, seen)).sort().join('|')}>`
    default:
      return t.kind
  }
}

/**
 * True when `expr` is safe to EMIT TWICE without re-running work or changing
 * meaning — a bare identifier, a signal/store READ (a zero-arg call like
 * `nums()` / `useApp()`, which lowers to a free @State / singleton read), or a
 * member/field access on such a base. Anything containing a method CALL WITH
 * ARGS (`filter(...)`, `map(...)`, `slice(...)`) is NOT re-readable — emitting
 * it twice would re-run that work. Used by the seedless-`.reduce(fn)` Swift
 * lowering (`obj.dropFirst().reduce(obj[0], fn)` names `obj` twice).
 */
/**
 * Does this access chain contain an OPTIONAL link (`a?.b` / `a?.[i]`)?
 * Syntactic — no inference dependency (the type layer doesn't yet wrap
 * optional-member results in a union, so a type-based check under-detects).
 * Used by the safe-index emit to route optional-RECEIVER chains: Kotlin
 * composes fully (`?.getOrNull`); Swift's guarded idiom can't (a `Bool?`
 * condition) and falls back to the warned nil-propagating subscript.
 */
export function exprHasOptionalLink(expr: ExprIR): boolean {
  if (expr.kind === 'member') {
    return expr.optional === true || exprHasOptionalLink(expr.object)
  }
  if (expr.kind === 'index') {
    return expr.optional === true || exprHasOptionalLink(expr.object)
  }
  if (expr.kind === 'call') return exprHasOptionalLink(expr.callee)
  if (expr.kind === 'paren') return exprHasOptionalLink(expr.inner)
  return false
}

export function isReReadableExpr(expr: ExprIR): boolean {
  // A scalar literal named twice is free — trivially re-readable.
  if (expr.kind === 'literal') return true
  if (expr.kind === 'identifier') return true
  // zero-arg call = signal / store / hook read — re-reading is free
  if (expr.kind === 'call' && expr.args.length === 0) return isReReadableExpr(expr.callee)
  // member/field access on a re-readable base (`obj.field`, `store.tasks`)
  if (expr.kind === 'member') return isReReadableExpr(expr.object)
  if (expr.kind === 'paren') return isReReadableExpr(expr.inner)
  return false
}

/**
 * True when `name` occurs as a FREE identifier anywhere in `expr`. Mirrors
 * `substituteIdentifier`'s total recursion (every node kind covered) but
 * returns a boolean and never mutates. A nested arrow that re-binds `name` in
 * its params shadows it — occurrences under that arrow are BOUND, not free, so
 * the subtree is skipped. Conservative for the callers that use it (the
 * `Array.from({length}, (el, i) => …)` element-param guard): a false "yes"
 * only defers a lowering to a warning, never mis-emits.
 */
export function exprReferencesIdent(expr: ExprIR, name: string): boolean {
  switch (expr.kind) {
    case 'new-collection':
      if (expr.seed !== undefined) return exprReferencesIdent(expr.seed, name)
      return (
        expr.entries?.some(
          ([k, v]) => exprReferencesIdent(k, name) || exprReferencesIdent(v, name),
        ) ?? false
      )
    case 'literal':
      return false
    case 'identifier':
      return expr.name === name
    case 'call':
      return (
        exprReferencesIdent(expr.callee, name) ||
        expr.args.some((a) => exprReferencesIdent(a, name))
      )
    case 'member':
      return exprReferencesIdent(expr.object, name)
    case 'index':
      return exprReferencesIdent(expr.object, name) || exprReferencesIdent(expr.index, name)
    case 'binary':
    case 'comparison':
    case 'logical':
      return exprReferencesIdent(expr.left, name) || exprReferencesIdent(expr.right, name)
    case 'unary':
    case 'update':
      return exprReferencesIdent(expr.argument, name)
    case 'ternary':
      return (
        exprReferencesIdent(expr.cond, name) ||
        exprReferencesIdent(expr.then, name) ||
        exprReferencesIdent(expr.otherwise, name)
      )
    case 'arrow':
      // Shadow boundary — a nested arrow re-binding `name` makes the inner
      // occurrences BOUND (a different variable), so they don't count.
      if (expr.params.includes(name)) return false
      return exprReferencesIdent(expr.body, name)
    case 'array':
      return expr.elements.some((el) => exprReferencesIdent(el, name))
    case 'template':
      return expr.exprs.some((ex) => exprReferencesIdent(ex, name))
    case 'object':
      return (
        expr.fields.some((f) => exprReferencesIdent(f.value, name)) ||
        (expr.spreads !== undefined && expr.spreads.some((sp) => exprReferencesIdent(sp, name)))
      )
    case 'paren':
      return exprReferencesIdent(expr.inner, name)
    case 'await':
      return exprReferencesIdent(expr.expr, name)
    case 'json-stringify':
      return exprReferencesIdent(expr.arg, name)
    case 'ext-expr':
      return expr.args.some((a) => exprReferencesIdent(a, name))
    case 'spread':
      return exprReferencesIdent(expr.argument, name)
    case 'jsx-element':
      return (
        expr.attrs.some((a) =>
          a.kind === 'attr'
            ? exprReferencesIdent(a.value, name)
            : a.kind === 'event'
              ? exprReferencesIdent(a.handler, name)
              : false,
        ) || childrenReferenceIdent(expr.children, name)
      )
    case 'jsx-fragment':
      return childrenReferenceIdent(expr.children, name)
  }
}

function childrenReferenceIdent(children: ChildIR[], name: string): boolean {
  // Only expression children carry identifiers; `text` children never do.
  // JSX inside these callbacks is nonsense, but the walker stays total so the
  // helper is reusable.
  for (const c of children) {
    if (c.kind !== 'text' && exprReferencesIdent(c.expr, name)) return true
  }
  return false
}

/**
 * Replace every free occurrence of identifier `name` in `expr` with
 * `replacement`. Returns null (CONSERVATIVE BAIL) when a nested arrow
 * shadows `name` — substituting inside the shadow would change
 * meaning, and distinguishing free-vs-bound occurrences across the
 * shadow boundary isn't worth the complexity for the `.update` shapes
 * real sources write (the param name and inner-callback param names
 * never collide in practice; a collision falls back to the caller's
 * no-lowering path with a warning).
 *
 * JSX subtrees recurse through attr/event/child expression slots —
 * JSX inside an `.update` callback is nonsense, but the walker stays
 * total so future callers can reuse it for other lowerings.
 */
export function substituteIdentifier(
  expr: ExprIR,
  name: string,
  replacement: ExprIR,
): ExprIR | null {
  return substituteMatching(expr, {
    matches: (e) => e.kind === 'identifier' && e.name === name,
    replacement,
    shadow: name,
  })
}

/**
 * What {@link substituteMatching} replaces. `matches` decides a node; `shadow`
 * is the root name a nested arrow parameter would rebind (a bail, as in
 * `substituteIdentifier`). `narrowing` is the optional-narrowing use: `x?.y`
 * with `x` replaced by its unwrapped binding becomes `x.y`, and an arrow with
 * a statement body — which the identifier walker passes through untouched —
 * is a bail rather than a silent miss.
 */
export interface Substitution {
  matches: (e: ExprIR) => boolean
  replacement: ExprIR
  shadow: string
  narrowing?: boolean
}

/** Total structural replace — the engine behind `substituteIdentifier`. */
export function substituteMatching(expr: ExprIR, subst: Substitution): ExprIR | null {
  const { replacement } = subst
  const name = subst.shadow
  if (subst.matches(expr)) return replacement
  switch (expr.kind) {
    case 'new-collection': {
      if (expr.seed !== undefined) {
        const seed = substituteMatching(expr.seed, subst)
        if (seed === null) return null // bail-propagation, like every other case
        return { ...expr, seed }
      }
      if (expr.entries !== undefined) {
        const entries: [ExprIR, ExprIR][] = []
        for (const [k, v] of expr.entries) {
          const nk = substituteMatching(k, subst)
          const nv = substituteMatching(v, subst)
          if (nk === null || nv === null) return null
          entries.push([nk, nv])
        }
        return { ...expr, entries }
      }
      return expr
    }
    case 'literal':
      return expr
    case 'identifier':
      return expr
    case 'call': {
      const callee = substituteMatching(expr.callee, subst)
      if (callee === null) return null
      const args: ExprIR[] = []
      for (const a of expr.args) {
        const sub = substituteMatching(a, subst)
        if (sub === null) return null
        args.push(sub)
      }
      return { ...expr, callee, args }
    }
    case 'member': {
      const hit = subst.narrowing === true && expr.optional === true && subst.matches(expr.object)
      const object = substituteMatching(expr.object, subst)
      if (object === null) return null
      return hit ? { ...expr, object, optional: false } : { ...expr, object }
    }
    case 'index': {
      const object = substituteMatching(expr.object, subst)
      if (object === null) return null
      const index = substituteMatching(expr.index, subst)
      if (index === null) return null
      return { ...expr, object, index }
    }
    case 'binary':
    case 'comparison':
    case 'logical': {
      const left = substituteMatching(expr.left, subst)
      if (left === null) return null
      const right = substituteMatching(expr.right, subst)
      if (right === null) return null
      return { ...expr, left, right }
    }
    case 'unary':
    case 'update': {
      const argument = substituteMatching(expr.argument, subst)
      if (argument === null) return null
      return { ...expr, argument }
    }
    case 'ternary': {
      const cond = substituteMatching(expr.cond, subst)
      if (cond === null) return null
      const then = substituteMatching(expr.then, subst)
      if (then === null) return null
      const otherwise = substituteMatching(expr.otherwise, subst)
      if (otherwise === null) return null
      return { ...expr, cond, then, otherwise }
    }
    case 'arrow': {
      // Shadow boundary — a nested arrow re-binding `name` makes the
      // inner occurrences BOUND, not free. Conservative bail (see doc).
      if (expr.params.includes(name)) return null
      if (subst.narrowing === true && expr.stmts !== undefined && expr.stmts.length > 0) return null
      const body = substituteMatching(expr.body, subst)
      if (body === null) return null
      return { ...expr, body }
    }
    case 'array': {
      const elements: ExprIR[] = []
      for (const el of expr.elements) {
        const sub = substituteMatching(el, subst)
        if (sub === null) return null
        elements.push(sub)
      }
      return { ...expr, elements }
    }
    case 'template': {
      // Template literal — substitute into each interpolated expression;
      // the literal quasi segments carry no identifiers.
      const exprs: ExprIR[] = []
      for (const ex of expr.exprs) {
        const sub = substituteMatching(ex, subst)
        if (sub === null) return null
        exprs.push(sub)
      }
      return { ...expr, exprs }
    }
    case 'object': {
      const fields: { name: string; value: ExprIR }[] = []
      for (const f of expr.fields) {
        const value = substituteMatching(f.value, subst)
        if (value === null) return null
        fields.push({ name: f.name, value })
      }
      let spreads: ExprIR[] | undefined
      if (expr.spreads !== undefined) {
        spreads = []
        for (const sp of expr.spreads) {
          const sub = substituteMatching(sp, subst)
          if (sub === null) return null
          spreads.push(sub)
        }
      }
      return spreads !== undefined
        ? { ...expr, fields, spreads }
        : { ...expr, fields }
    }
    case 'paren': {
      const inner = substituteMatching(expr.inner, subst)
      if (inner === null) return null
      return { ...expr, inner }
    }
    case 'json-stringify': {
      const arg = substituteMatching(expr.arg, subst)
      if (arg === null) return null
      return { ...expr, arg }
    }
    case 'await': {
      // M4.5: `await X` — substitute inside the awaited expr (single-wrapper,
      // like `paren`, but the child slot is `.expr`).
      const inner = substituteMatching(expr.expr, subst)
      if (inner === null) return null
      return { ...expr, expr: inner }
    }
    case 'ext-expr': {
      const args: ExprIR[] = []
      for (const a of expr.args) {
        const next = substituteMatching(a, subst)
        if (next === null) return null
        args.push(next)
      }
      return { ...expr, args }
    }
    case 'spread': {
      const argument = substituteMatching(expr.argument, subst)
      if (argument === null) return null
      return { ...expr, argument }
    }
    case 'jsx-element': {
      const attrs: AttrIR[] = []
      for (const a of expr.attrs) {
        if (a.kind === 'attr') {
          const value = substituteMatching(a.value, subst)
          if (value === null) return null
          attrs.push({ ...a, value })
        } else if (a.kind === 'event') {
          const handler = substituteMatching(a.handler, subst)
          if (handler === null) return null
          attrs.push({ ...a, handler })
        } else {
          attrs.push(a)
        }
      }
      const children = substituteInChildren(expr.children, subst)
      if (children === null) return null
      return { ...expr, attrs, children }
    }
    case 'jsx-fragment': {
      const children = substituteInChildren(expr.children, subst)
      if (children === null) return null
      return { ...expr, children }
    }
  }
}

/**
 * Lower a route `loader: (ctx) => …ctx.params.X…` body's param reads to a
 * native dict read on a synthetic `params` binding. `ctx.params.id` and
 * `ctx.params["id"]` both become `index(identifier('params'), 'id')`, which
 * the emitters render as `params["id"]` — and the route-dispatch branch
 * already binds `params` from `matchPath(path, "/x/:id")` in scope.
 *
 * Returns the rewritten expr plus two flags: `usesParams` (the body read at
 * least one `ctx.params.*`, so the emit must bind `params` even if the
 * component prop doesn't) and `residualCtx` (the body referenced `ctx` in some
 * OTHER way — `ctx.request`, a bare `ctx`, etc. — which v1 does NOT support;
 * the caller drops the loader + warns rather than emit an unbound `ctx`).
 *
 * Mirrors `substituteIdentifier`'s total recursion so every node kind is
 * covered; only `member`/`index` whose object is `ctx.params` are special.
 */
export interface LowerParamsResult {
  expr: ExprIR
  usesParams: boolean
  residualCtx: boolean
}
export function lowerRouteParams(expr: ExprIR, ctxName: string): LowerParamsResult {
  const flags = { usesParams: false, residualCtx: false }
  const out = walkLowerParams(expr, ctxName, flags)
  return { expr: out, usesParams: flags.usesParams, residualCtx: flags.residualCtx }
}

// `(params[<key>] ?? "")` — a native dict read defaulted to "" so it matches
// the web `string` type of `ctx.params.x` (a bare `params["id"]` is `String?`
// on both targets, which mismatches non-optional consumers). The `??`
// lowers to Swift `??` and Kotlin Elvis `?:`.
function paramRead(key: ExprIR): ExprIR {
  return {
    kind: 'logical',
    op: '??',
    left: { kind: 'index', object: { kind: 'identifier', name: 'params' }, index: key },
    right: { kind: 'literal', value: '' },
  }
}

// True when `e` is the `ctx.params` member access (the read root we rewrite).
function isCtxParams(e: ExprIR, ctxName: string): boolean {
  return (
    e.kind === 'member' &&
    e.property === 'params' &&
    e.object.kind === 'identifier' &&
    e.object.name === ctxName
  )
}

function walkLowerParams(
  expr: ExprIR,
  ctxName: string,
  flags: { usesParams: boolean; residualCtx: boolean },
): ExprIR {
  const rec = (e: ExprIR): ExprIR => walkLowerParams(e, ctxName, flags)
  switch (expr.kind) {
    case 'new-collection':
      if (expr.seed !== undefined) return { ...expr, seed: rec(expr.seed) }
      if (expr.entries !== undefined) {
        return {
          ...expr,
          entries: expr.entries.map(([k, v]): [ExprIR, ExprIR] => [rec(k), rec(v)]),
        }
      }
      return expr
    case 'literal':
      return expr
    case 'identifier':
      // A bare `ctx` (not part of a `ctx.params` access) is unsupported.
      if (expr.name === ctxName) flags.residualCtx = true
      return expr
    case 'member': {
      // `ctx.params.id` → `(params["id"] ?? "")`.
      if (isCtxParams(expr.object, ctxName)) {
        flags.usesParams = true
        return paramRead({ kind: 'literal', value: expr.property })
      }
      return { ...expr, object: rec(expr.object) }
    }
    case 'index': {
      // `ctx.params["id"]` → `(params["id"] ?? "")`.
      if (isCtxParams(expr.object, ctxName)) {
        flags.usesParams = true
        return paramRead(rec(expr.index))
      }
      return { ...expr, object: rec(expr.object), index: rec(expr.index) }
    }
    case 'call':
      return { ...expr, callee: rec(expr.callee), args: expr.args.map(rec) }
    case 'binary':
    case 'comparison':
    case 'logical':
      return { ...expr, left: rec(expr.left), right: rec(expr.right) }
    case 'unary':
    case 'update':
      return { ...expr, argument: rec(expr.argument) }
    case 'ternary':
      return { ...expr, cond: rec(expr.cond), then: rec(expr.then), otherwise: rec(expr.otherwise) }
    case 'arrow':
      return { ...expr, body: rec(expr.body) }
    case 'array':
      return { ...expr, elements: expr.elements.map(rec) }
    case 'template':
      return { ...expr, exprs: expr.exprs.map(rec) }
    case 'object': {
      const fields = expr.fields.map((f) => ({ name: f.name, value: rec(f.value) }))
      return expr.spreads !== undefined
        ? { ...expr, fields, spreads: expr.spreads.map(rec) }
        : { ...expr, fields }
    }
    case 'paren':
      return { ...expr, inner: rec(expr.inner) }
    case 'await':
      return { ...expr, expr: rec(expr.expr) }
    case 'json-stringify':
      return { ...expr, arg: rec(expr.arg) }
    case 'ext-expr':
      return { ...expr, args: expr.args.map(rec) }
    case 'spread':
      return { ...expr, argument: rec(expr.argument) }
    case 'jsx-element':
    case 'jsx-fragment':
      // A loader body is not JSX; leave these untouched (total-walker safety).
      return expr
  }
}

function substituteInChildren(children: ChildIR[], subst: Substitution): ChildIR[] | null {
  const out: ChildIR[] = []
  for (const c of children) {
    if (c.kind === 'text') {
      out.push(c)
      continue
    }
    const next = substituteMatching(c.expr, subst)
    if (next === null) return null
    out.push({ ...c, expr: next })
  }
  return out
}

/**
 * Classify a styling attr's DYNAMIC value (called only after the static
 * path — literal / resolvable const via readStaticAttr — came up empty).
 * Styling tokens (`gap`/`pad`/`background`/`radius`) resolve at COMPILE
 * time, so the only faithful dynamic form is a TERNARY OF TWO LITERALS
 * (`gap={dense() ? "sm" : "lg"}` — the binary-state idiom): both
 * branches compile-resolve and the condition emits natively. Anything
 * else is `dynamic` — the caller warns loudly (pre-fix the whole
 * modifier was SILENTLY dropped).
 */
export function classifyDynamicStylingAttr(
  e: Extract<ExprIR, { kind: 'jsx-element' }>,
  name: string,
):
  | { kind: 'none' }
  | { kind: 'ternary'; cond: ExprIR; a: string | number; b: string | number }
  | { kind: 'dynamic' } {
  for (const a of e.attrs) {
    if (a.kind === 'attr' && a.name === name) {
      const v = a.value
      if (v.kind === 'literal') return { kind: 'none' }
      if (
        v.kind === 'ternary' &&
        v.then.kind === 'literal' &&
        v.otherwise.kind === 'literal' &&
        (typeof v.then.value === 'string' || typeof v.then.value === 'number') &&
        (typeof v.otherwise.value === 'string' || typeof v.otherwise.value === 'number')
      ) {
        return { kind: 'ternary', cond: v.cond, a: v.then.value, b: v.otherwise.value }
      }
      return { kind: 'dynamic' }
    }
  }
  return { kind: 'none' }
}

/**
 * Structural key identifying an object shape, used by BOTH emitters to resolve
 * an object type / object literal to a DECLARED struct of the same shape (so a
 * prop typed `{ id, text, done }` and a literal constructing it agree on one
 * nominal type — the user's own `Todo` rather than a synthesized twin).
 *
 * The key includes each field's TYPE, not just its name. Keying on names alone
 * collapses two declared types that share field names and differ in field
 * types, and the FIRST one registered wins for both:
 *
 *     type Px  = { x: Double; y: Double }
 *     type Idx = { x: number; y: number }
 *     const i: Idx = { x: 1, y: 2 }        // emitted `Px(x: 1, y: 2)`
 *
 * The declared annotation was ignored and the wrong struct constructed —
 * silently where the field types happen to coerce, and as a hard
 * `cannot convert value of type 'Double' to expected argument type 'Int'`
 * where they do not. A geometry library cannot avoid this: a point, an anchor,
 * an offset and a tick position are all `{ x, y }`.
 *
 * The serialization is BACKEND-NEUTRAL (built from the IR, not from
 * `swiftType`/`kotlinType`) so the two emitters can never disagree about which
 * declared struct a shape resolves to.
 */
export function structShapeKey(

  fields: readonly { name: string; type: TypeIR }[],
  resolve?: StructResolver,
  seen?: ReadonlySet<string>,
): string {
  return fields
    .map((f) => `${f.name}:${typeShapeKey(f.type, resolve, seen)}`)
    .sort()
    .join(',')
}

/**
 * Typed shape key for an object LITERAL, derived from its own values.
 *
 * The literal site knows field names and value EXPRESSIONS, not declared
 * types — so it cannot use `structShapeKey` directly, and keying on names
 * alone silently picks the first declared struct of that shape (see
 * `structShapeKey`). Reading the literal's own scalar values recovers enough
 * to disambiguate the case that matters: `{ x: 1.5, y: 2.5 }` keys as two
 * doubles and resolves to `Px`, `{ x: 1, y: 2 }` keys as two ints and
 * resolves to `Idx`.
 *
 * Returns `null` when ANY field's type is not locally decidable (a call, an
 * identifier, a member read). Callers fall back to the name-only lookup, so
 * this can only ever ADD a correct match — never remove one that works today.
 */
export function literalShapeKey(fields: readonly { name: string; value: ExprIR }[]): string | null {
  const parts: string[] = []
  for (const f of fields) {
    const v = f.value
    if (v.kind !== 'literal') return null
    if (typeof v.value === 'number') {
      // `float` on the IR node wins when present — a `0.0` written by the
      // author is a Double even though its VALUE is integral.
      const isFloat = v.float === true || !Number.isInteger(v.value)
      parts.push(`${f.name}:${isFloat ? 'number.f' : 'number'}`)
    } else if (typeof v.value === 'string') parts.push(`${f.name}:string`)
    else if (typeof v.value === 'boolean') parts.push(`${f.name}:boolean`)
    else return null
  }
  return parts.sort().join(',')
}

/**
 * What key a `<For>` should use when the source gave no `by` callback.
 *
 * `<For each={items}>` with no `by` defaulted to SwiftUI `id: \.id` /
 * Compose `key = { it.id }` — correct for a record list and an UNCOMPILABLE
 * SILENT mis-emit for anything else: `value of type 'String' has no member
 * 'id'` (swiftc) / `unresolved reference 'id'` (kotlinc) for a `string[]` or
 * `number[]`, and the same for a struct that simply has no `id` field.
 *
 * This is the SAME defect PR #1984 fixed one guard later — there for an
 * identity `by={(x) => x}` callback, whose own comment calls it "an
 * uncompilable SILENT mis-emit". The no-`by` spelling was left behind, so the
 * fix stayed folklore about one shape instead of becoming the rule about the
 * class. `by` is optional in the JSX types, so omitting it is the FIRST thing
 * someone writes.
 *
 * Verdicts, and why `unknown` stays silent: a warning is only emitted when the
 * element type PROVES there is no `id` to key on. An element the inferer cannot
 * resolve keeps the historical `.id` fallback with no warning — a record list
 * whose type is merely invisible here is the common correct case, and warning
 * on it would train people to ignore the warning that matters.
 */
export type ForKeyVerdict =
  /** A primitive element — key on the value itself (`\.self` / `it`). */
  | { kind: 'self' }
  /** The element provably carries an `id` field — the historical default is right. */
  | { kind: 'id' }
  /** Not resolvable here — keep `.id`, say nothing. */
  | { kind: 'unknown' }
  /** Provably has NO `id` — the caller warns, naming `what`. */
  | { kind: 'no-id'; what: string }

export function resolveForElementKey(
  elem: TypeIR | undefined,
  structs: ReadonlyMap<string, ReadonlyMap<string, TypeIR>>,
): ForKeyVerdict {
  if (elem === undefined) return { kind: 'unknown' }
  switch (elem.kind) {
    case 'string':
    case 'number':
    case 'boolean':
      // Hashable on both targets — key on the element itself.
      return { kind: 'self' }
    case 'object':
      return elem.fields.some((f) => f.name === 'id')
        ? { kind: 'id' }
        : {
            kind: 'no-id',
            what: `an object with fields (${elem.fields.map((f) => f.name).join(', ') || 'none'})`,
          }
    case 'typeRef': {
      const fields = structs.get(elem.name)
      // An UNKNOWN name is not a proof of absence — PMTC does not follow
      // imports, so a type declared elsewhere lands here with no fields.
      if (fields === undefined) return { kind: 'unknown' }
      return fields.has('id') ? { kind: 'id' } : { kind: 'no-id', what: `\`${elem.name}\`, which has no \`id\` field` }
    }
    default:
      return { kind: 'unknown' }
  }
}

/** The shared text for both targets, so the two can never drift apart. */
export function forMissingByWarning(what: string, target: 'swift' | 'kotlin'): string {
  const emitted = target === 'swift' ? 'id: \\.id' : 'key = { it.id }'
  return (
    `<For each={…}> without a \`by\` key: the element is ${what}, so the default ` +
    `\`${emitted}\` does not compile on ${target === 'swift' ? 'iOS' : 'Android'}. ` +
    `Add \`by={(x) => x}\` to key on the element itself, or \`by={(x) => x.someField}\` ` +
    `to key on a field.`
  )
}

/**
 * Name a PROVABLY non-Bool operand of a `&&` / `||`, or `undefined` when both
 * sides are boolean-or-unresolvable. See `nonBooleanLogicalWarning` for why
 * this warns rather than desugars, and why `unknown` must stay silent.
 *
 * `null` / `undefined` operands are excluded on purpose: `x && y` where `x` is
 * nullable is a shape the OPTIONAL machinery already handles at the condition
 * seam, and flagging it here would fire on correct code.
 */
export function classifyNonBooleanLogicalOperand(t: TypeIR): { name: string } | undefined {
  switch (t.kind) {
    case 'string':
      return { name: 'string' }
    case 'number':
      return { name: 'number' }
    case 'array':
      return { name: 'array' }
    case 'map':
      return { name: 'map' }
    case 'set':
      return { name: 'set' }
    case 'object':
      return { name: 'object' }
    case 'typeRef':
      // A named struct / data class. `Promise` is excluded — an awaited value
      // is not a truthiness test anyone writes here.
      return t.name === 'Promise' ? undefined : { name: t.name }
    default:
      // boolean, union, function, null, undefined, unknown — silent.
      return undefined
  }
}

/**
 * Does this expression contain a JSX node ANYWHERE inside it?
 *
 * Written as a TOTAL walk (mirroring `exprReferencesIdent`'s discipline) rather
 * than the shallow `*ProducesView` predicates, whose whole job is the opposite
 * question: "is the RESULT of this expression a view?".
 *
 * The gap between the two is where `{items.map((i) => <Text>{i}</Text>)}` fell.
 * A `call` is not view-producing, so both emitters routed it to the
 * stringification arm and emitted Swift `Text(verbatim: "\\(items.map({ … }))")`
 * / Kotlin `Text(text = "${items.map { … }}")` — the JSX children built and then
 * INTERPOLATED INTO A STRING, on both targets, with zero warnings. `<For>` is
 * the spelling that works, and nothing said so.
 *
 * Deliberately conservative in ONE direction only: it answers "is there JSX in
 * here", which is a sound trigger for a warning because a stringified
 * expression that builds views is never what the author meant.
 */
export function exprContainsJsx(e: ExprIR): boolean {
  switch (e.kind) {
    case 'jsx-element':
    case 'jsx-fragment':
      return true
    case 'paren':
      return exprContainsJsx(e.inner)
    case 'call':
      return exprContainsJsx(e.callee) || e.args.some(exprContainsJsx)
    case 'arrow':
      return (
        (e.body !== undefined && exprContainsJsx(e.body)) ||
        (e.stmts ?? []).some(stmtContainsJsx)
      )
    case 'member':
      return exprContainsJsx(e.object)
    case 'index':
      return exprContainsJsx(e.object) || exprContainsJsx(e.index)
    case 'logical':
      return exprContainsJsx(e.left) || exprContainsJsx(e.right)
    case 'binary':
    case 'comparison':
      return exprContainsJsx(e.left) || exprContainsJsx(e.right)
    case 'ternary':
      return exprContainsJsx(e.cond) || exprContainsJsx(e.then) || exprContainsJsx(e.otherwise)
    case 'unary':
      return exprContainsJsx(e.argument)
    case 'await':
      return exprContainsJsx(e.expr)
    case 'array':
      return e.elements.some(exprContainsJsx)
    case 'object':
      return (
        e.fields.some((f) => exprContainsJsx(f.value)) ||
        (e.spreads ?? []).some(exprContainsJsx)
      )
    case 'spread':
      return exprContainsJsx(e.argument)
    case 'template':
      return e.exprs.some(exprContainsJsx)
    default:
      // literal / identifier / update / json-stringify /
      // new-collection —
      // none can carry JSX in the shapes PMTC parses.
      return false
  }
}

function stmtContainsJsx(s: { kind: string; expr?: ExprIR }): boolean {
  return s.expr !== undefined && exprContainsJsx(s.expr)
}

/**
 * `<For>` is the only list spelling that lowers. Shared so the two targets can
 * never drift on the remedy they name.
 */
export function jsxInStringifiedChildWarning(target: 'swift' | 'kotlin'): string {
  const emitted = target === 'swift' ? 'Text(verbatim: "\\(…)")' : 'Text(text = "${…}")'
  return (
    `A JSX-producing expression used as a child does NOT lower to a list on iOS or Android — ` +
    `it is interpolated into a string (\`${emitted}\`), so the elements are rendered as their ` +
    `debug description instead of as views. \`{items.map((x) => <Row … />)}\` is the shape that ` +
    `hits this; use \`<For each={items} by={(x) => x.id}>{(x) => <Row … />}</For>\`, which lowers ` +
    `to SwiftUI \`ForEach\` / Compose \`items()\`.`
  )
}

/**
 * `T | null` / `T | undefined` — the shape both emitters lower to a Swift
 * Optional or a Kotlin nullable. Flat by construction (the parser does not
 * nest unions), so a single `some` is the whole test.
 */
export function isNullableType(t: TypeIR | undefined): boolean {
  return t?.kind === 'union' && t.branches.some((b) => b.kind === 'null' || b.kind === 'undefined')
}

/**
 * Spreading an OPTIONAL object emits code neither toolchain accepts, and until
 * this warning existed it did so in SILENCE on both targets:
 *
 *   swift   func f(_ o: Opts?) -> Opts { { var c = o; c.a = "x"; return c }() }
 *   kotlin  fun f(o: Opts?): Opts = o.copy(a = "x")
 *
 * `c` is `Opts?`, so the member assignment and the return type both fail;
 * `.copy` on a nullable receiver is rejected outright. TypeScript is perfectly
 * happy with the source — `{ ...undefined }` is legal and contributes nothing —
 * so the FIRST signal was a swiftc/kotlinc gate, whose error text points at
 * generated Swift rather than at the line that produced it. One such spread in
 * `candlestick-chart.ts` cost 35 compile failures across every chart suite.
 *
 * There is no honest lowering to fall back to: JS's semantics say the spread
 * contributes nothing when the source is nullish, but the emitted struct still
 * needs every field, and the defaults for the ones the literal does not name
 * are not knowable here. So this NAMES the problem and the emit is unchanged —
 * a diagnostic, not a behaviour change.
 */
export function optionalSpreadWarning(name: string): string {
  return (
    `Spreading \`${name}\`, which is optional, has no native lowering: the emit assigns through a Swift Optional / calls \`.copy\` on a Kotlin nullable, and neither compiles. ` +
    `Build the object field by field instead (\`field: ${name}?.field ?? <fallback>\`), or narrow \`${name}\` to non-optional before the spread.`
  )
}

/**
 * A numeric literal, or a negated one. PMTC parses `-2` as a unary minus over
 * `2`, so a reader that asks only `kind === 'literal'` treats every negative
 * constant as a runtime expression and emits a conversion around it.
 */
export function isNumericLiteralOrNegation(x: ExprIR): boolean {
  if (x.kind === 'literal') return typeof x.value === 'number' || typeof x.value === 'string'
  return (
    x.kind === 'unary' &&
    (x.op === '-' || x.op === '+') &&
    x.argument.kind === 'literal' &&
    typeof x.argument.value === 'number'
  )
}
